import { create } from 'zustand';
import { env } from '@/utils/constants/env';
import { toast } from 'react-toastify';
import { errorHandler } from '@/utils/functions/errorHandler';
import useAuthState from '@/states/auth.state';
import useTenantState from '@/states/tenant.state';
import axios from 'axios';
import { axiosTasklist } from '@/utils/lib/axiosTasklist';

export interface TenantSetting {
  id: number;
  tenant_id: number;
  theme: string;
  primary_color: string;
  secondary_color: string;
  favicon_path: string;
  created_at: Date;
  updated_at: Date;
}

interface Theme {
  name: string;
  colorClass: string;
  colorText: string;
  colorHover: string;

  tableHeaderClass: string;
  headerClass: string;
  headerContentClass: string;
  headerActiveItemClass: string;
  headerButtonHoverClass: string;
  sidebarActiveClass: string;
  sidebarHoverClass: string;
  switcherActiveContentClass: string;
}

type ThemeName = 'default' | 'green' | 'orange' | 'purple' | 'red';

const themes: Record<ThemeName, Theme> = {
  default: {
    name: 'default',
    colorClass: 'bg-primary hover:bg-secondary',
    colorText: 'text-primary bg-active hover:bg-active',
    colorHover: 'hover:bg-active',
    tableHeaderClass: 'bg-active',
    headerClass: 'bg-white border-b',
    headerContentClass: 'text-gray-500',
    headerActiveItemClass:
      'hover:bg-blue-100 hover:text-[#226ABE] hover:border-[#226ABE] border-[#226ABE] text-[#226ABE]',
    headerButtonHoverClass: 'hover:bg-gray-100',
    sidebarActiveClass: 'bg-active text-primary',
    sidebarHoverClass: 'hover:bg-active hover:text-primary',
    switcherActiveContentClass: 'text-primary',
  },
  green: {
    name: 'green',
    colorClass: 'bg-green-alurkerja hover:bg-green-alurkerja-hovered',
    colorText: 'text-green-alurkerja bg-green-sidebar',
    colorHover: 'hover:bg-green-sidebar',
    tableHeaderClass: 'bg-green-table',
    headerClass: 'bg-white border-b',
    headerContentClass: 'text-gray-500',
    headerActiveItemClass:
      'hover:bg-blue-100 hover:text-[#226ABE] hover:border-[#226ABE] border-[#226ABE] text-[#226ABE]',
    headerButtonHoverClass: 'hover:bg-gray-100',
    sidebarActiveClass: 'bg-green-sidebar text-green-alurkerja',
    sidebarHoverClass: 'hover:bg-green-sidebar hover:text-green-alurkerja',
    switcherActiveContentClass: 'text-green-alurkerja',
  },
  orange: {
    name: 'orange',
    colorClass: 'bg-orange-alurkerja hover:bg-orange-alurkerja-hovered',
    colorText: 'text-orange-alurkerja bg-orange-sidebar',
    colorHover: 'hover:bg-orange-sidebar',
    tableHeaderClass: 'bg-orange-table',
    headerClass: 'bg-white border-b',
    headerContentClass: 'text-gray-500',
    headerActiveItemClass:
      'hover:bg-blue-100 hover:text-[#226ABE] hover:border-[#226ABE] border-[#226ABE] text-[#226ABE]',
    headerButtonHoverClass: 'hover:bg-gray-100',
    sidebarActiveClass: 'bg-orange-sidebar text-orange-alurkerja',
    sidebarHoverClass: 'hover:bg-orange-sidebar hover:text-orange-alurkerja',
    switcherActiveContentClass: 'text-orange-alurkerja',
  },
  purple: {
    name: 'purple',
    colorClass: 'bg-purple-alurkerja hover:bg-purple-alurkerja-hovered',
    colorText: 'text-purple-alurkerja bg-purple-sidebar',
    colorHover: 'hover:bg-purple-sidebar',
    tableHeaderClass: 'bg-purple-table',
    headerClass: 'bg-white border-b',
    headerContentClass: 'text-gray-500',
    headerActiveItemClass:
      'hover:bg-blue-100 hover:text-[#226ABE] hover:border-[#226ABE] border-[#226ABE] text-[#226ABE]',
    headerButtonHoverClass: 'hover:bg-gray-100',
    sidebarActiveClass: 'bg-purple-sidebar text-purple-alurkerja',
    sidebarHoverClass: 'hover:bg-purple-sidebar hover:text-purple-alurkerja',
    switcherActiveContentClass: 'text-purple-alurkerja',
  },
  red: {
    name: 'red',
    colorClass: 'bg-red-studio hover:bg-red-studio-hovered',
    colorText: 'text-red-studio bg-red-sidebar',
    colorHover: 'hover:bg-red-sidebar',
    tableHeaderClass: 'bg-red-table',
    headerClass: 'bg-red-studio border-b border-red-studio',
    headerContentClass: 'text-white',
    headerActiveItemClass: 'hover:bg-white/10 hover:text-white hover:border-white border-white text-white',
    headerButtonHoverClass: 'hover:bg-white/10',
    sidebarActiveClass: 'bg-red-sidebar text-red-studio',
    sidebarHoverClass: 'hover:bg-red-sidebar hover:text-red-studio',
    switcherActiveContentClass: 'text-red-studio',
  },
};

interface TenantSettingState {
  isLoading: boolean;
  error: unknown;
  updateTenantTheme: (tenantId: number, data: FormData) => Promise<void>;
  getTenantTheme: (tenantId: number) => Promise<void>;
  tenantSetting: TenantSetting;
  currentTheme: Theme;
}

const useTenantSettingState = create<TenantSettingState>(set => ({
  isLoading: false,
  error: null,
  currentTheme: themes['default'],

  getTenantTheme: async tenantId => {
    set({ isLoading: true, error: null });

    try {
      const token = await useAuthState.getState().getToken();

      const url = new URL(`${env.apiTasklistBaseUrl}/api/v1/tenant/tenants/${tenantId}/theme`);

      const res = await axios.get(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-ACTIVE-TENANT': useTenantState.getState().currentTenant?.slug ?? '',
        },
      });

      const data = res.data.data;

      set({ tenantSetting: data });

      const { currentTenant, setCurrentTenant } = useTenantState.getState();

      if (currentTenant && currentTenant.id === data.tenant_id) {
        const updatedTenant = {
          ...currentTenant,
          logoPath: data.logo_path,
          faviconPath: data.favicon_path,
        };

        setCurrentTenant(updatedTenant);

        set({ currentTheme: themes[data.theme as ThemeName] || themes['default'] });

        if (data.favicon_path) 
          {const faviconUrl = `${axiosTasklist.defaults.cloudStorage}${data.favicon_path}`;

          let link = document.querySelector("link[rel='icon']") as HTMLLinkElement;

          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }

          link.href = faviconUrl;
        }
      }
    } catch (error) {
      console.error('❌ Error fetching tenant theme:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  updateTenantTheme: async (tenantId, data) => {
    set({ isLoading: true, error: null });
    const url = new URL(`${env.apiTasklistBaseUrl}/api/v1/tenant/tenants/${tenantId}/theme`);
    const token = await useAuthState.getState().getToken();

    return axios
      .post(url.toString(), data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${token ?? ''}`,
          'X-ACTIVE-TENANT': useTenantState.getState().currentTenant?.slug ?? '',
        },
      })
      .then(res => {
        toast.success('Tenant theme updated successfully!');
        console.log('✅ Tenant theme updated:', res.data);

        const updatedTenant = res.data.data?.tenant;
        const logoPath = res.data.data?.logo_path;
        const faviconPath = res.data.data?.favicon_path;

        const { loadTenants } = useAuthState.getState();
        loadTenants();

        useTenantSettingState.getState().getTenantTheme(tenantId);

        const { currentTenant, setCurrentTenant } = useTenantState.getState();
        if (currentTenant && updatedTenant && currentTenant.id === updatedTenant.id) {
          setCurrentTenant({
            ...currentTenant,
            ...updatedTenant,
            logoPath,
            faviconPath,
          });
        }
      })
      .catch(error => {
        console.error('❌ Error updating tenant theme:', error);
        errorHandler(error);
        set({ error });
        toast.error('Failed to update tenant theme.');
      })
      .finally(() => {
        set({ isLoading: false });
      });
  },
  tenantSetting: {} as TenantSetting,
}));

export default useTenantSettingState;
